/**
 * When the member chat composer may offer Generate.
 *
 * The mutation is sco-admin only, and it only drafts a direct chat. The account is whose
 * responder tool writes the reply. Prefer the person at the keyboard when they are that
 * account, so the voice matches who this box sends as. Otherwise the other person in the
 * chat who has a reply tool. Two of those is ambiguous, so the button stays off.
 */

import type { GenerateDraftRequest } from '@src/components/sco/GenerateReply/generateReply'
import type { GenerateCrmReplyInput } from '@src/graphql-types'

export type ChatGenerateParticipant = {
  responderToolId?: string | null
  userId?: string | null
}

export function chatGenerateAccountUserId(
  currentUserId: string | null | undefined,
  participants: Array<ChatGenerateParticipant | null | undefined>
): string | null {
  const withTool = participants.filter(
    (participant): participant is ChatGenerateParticipant & { responderToolId: string; userId: string } =>
      Boolean(participant?.userId && participant.responderToolId)
  )
  const self = currentUserId ? withTool.find(participant => participant.userId === currentUserId) : undefined
  if (self) {
    return self.userId
  }
  const others = withTool.filter(participant => participant.userId !== currentUserId)
  if (others.length === 1) {
    return others[0].userId
  }
  return null
}

export type ChatGenerateToolRow = {
  audienceKey?: string | null
  slug?: string | null
  title?: string | null
  toolId?: string | null
  toolTypeKey?: string | null
}

export type ChatGenerateToolChoice = {
  label: string
  toolId: string
}

/**
 * Chat tools the Generate box can run through, besides this account's own reply.
 * Member-facing and responder tools stay. Admin-only tools, apps, and the reply tool
 * already used by "This reply" stay out.
 */
export function chatGenerateToolChoices(
  tools: Array<ChatGenerateToolRow | null | undefined> | null | undefined,
  excludeToolId?: string | null
): ChatGenerateToolChoice[] {
  const seen = new Set<string>()
  const choices: ChatGenerateToolChoice[] = []
  for (const tool of tools || []) {
    const toolId = (tool?.toolId || '').trim()
    if (!toolId || seen.has(toolId)) {
      continue
    }
    if (excludeToolId && toolId === excludeToolId) {
      continue
    }
    const audience = tool?.audienceKey || ''
    if (audience !== 'members' && audience !== 'responder') {
      continue
    }
    if (tool?.toolTypeKey === 'app') {
      continue
    }
    seen.add(toolId)
    const label = (tool?.title || tool?.slug || 'Chat tool').trim() || 'Chat tool'
    choices.push({ label, toolId })
  }
  choices.sort(
    (left, right) =>
      left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }) ||
      left.toolId.localeCompare(right.toolId)
  )
  return choices
}

export function chatGenerateOffered(input: {
  accountUserId?: string | null
  isScoAdmin?: boolean | null
  parentCommentId?: string | null
  threadTypeKey?: string | null
}): boolean {
  return Boolean(
    input.isScoAdmin && !input.parentCommentId && input.threadTypeKey === 'direct-chat' && input.accountUserId
  )
}

/**
 * The `generateCrmReply` input for what the dialog chose. This button's contract is frozen: scope,
 * includeFiles, and only when set, the instruction and the chat tool to run through.
 */
export function chatGenerateReplyInput(input: {
  accountUserId: string
  request: GenerateDraftRequest
  threadId: string
}): GenerateCrmReplyInput {
  const instruction = input.request.instruction.trim()
  return {
    accountUserId: input.accountUserId,
    includeFiles: input.request.includeFiles,
    scope: input.request.scope,
    threadId: input.threadId,
    ...(instruction ? { instruction } : {}),
    ...(input.request.toolId ? { toolId: input.request.toolId } : {}),
  }
}

export type ChatGenerateSourceComment = {
  body?: string | null
  commentId?: string | null
  createdAt?: string | null
  createdByUserId?: string | null
  deletedAt?: string | null
  isDraft?: boolean | null
  storedFiles?: { items?: Array<{ deletedAt?: string | null } | null> | null } | null
  withdrawnAt?: string | null
}

export type ChatGenerateDialogComment = {
  body: string
  direction: 'in' | 'out'
  files: Array<{ deletedAt?: string | null }>
  isDraft: boolean
  withdrawnAt: string | null
}

function timeMs(value?: string | null): number {
  const ms = new Date(value || 0).getTime()
  return Number.isFinite(ms) ? ms : 0
}

/** Top-level chat rows, oldest first, in the shape the Generate dialog already reads. */
export function chatCommentsForGenerateDialog(
  comments: Array<ChatGenerateSourceComment | null | undefined> | null | undefined,
  accountUserId: string
): ChatGenerateDialogComment[] {
  const rows = (comments || []).filter(
    (comment): comment is ChatGenerateSourceComment => Boolean(comment) && !comment?.deletedAt
  )
  rows.sort((left, right) => {
    const delta = timeMs(left.createdAt) - timeMs(right.createdAt)
    if (delta !== 0) {
      return delta
    }
    return String(left.commentId || '').localeCompare(String(right.commentId || ''))
  })
  return rows.map(comment => ({
    body: comment.body || '',
    direction: accountUserId && comment.createdByUserId === accountUserId ? 'out' : 'in',
    files: (comment.storedFiles?.items || []).filter(
      (file): file is { deletedAt?: string | null } => Boolean(file) && !file?.deletedAt
    ),
    isDraft: comment.isDraft === true,
    withdrawnAt: comment.withdrawnAt || null,
  }))
}
